import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, TrendingDown, Download, Plus, X, Edit, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filterType, setFilterType] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [stats, setStats] = useState({ income: 0, expenses: 0 });
  const [formData, setFormData] = useState({
    entity_id: '', type: 'income', category: 'rent', description: '',
    amount: '', date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer'
  });

  useEffect(() => { 
    fetchProperties();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, filterType, filterProperty]);

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/corporations`);
      setProperties(res.data);
    } catch (error) { 
      console.error('Failed to fetch properties:', error); 
    }
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterType) params.append('transaction_type', filterType);
      if (filterProperty) params.append('entity_id', filterProperty);
      
      const res = await axios.get(`${API}/transactions?${params}`);
      
      // Handle both paginated and non-paginated responses
      if (res.data.data) {
        setTransactions(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
        
        // Calculate stats from current page
        const income = res.data.data.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
        const expenses = res.data.data.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
        setStats({ income, expenses });
      } else {
        // Fallback for non-paginated response
        setTransactions(res.data);
        setPagination(prev => ({ ...prev, total: res.data.length, pages: 1 }));
        const income = res.data.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
        const expenses = res.data.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
        setStats({ income, expenses });
      }
    } catch (error) { 
      console.error('Failed to fetch transactions:', error); 
    } finally { 
      setLoading(false); 
    }
  }, [pagination.page, pagination.limit, filterType, filterProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, amount: parseFloat(formData.amount) };
      if (editingTx) {
        await axios.put(`${API}/transactions/${editingTx.id}`, data);
      } else {
        await axios.post(`${API}/transactions`, data);
      }
      setShowModal(false);
      resetForm();
      fetchTransactions();
    } catch (error) { console.error('Failed to save transaction:', error); }
  };

  const openEditModal = (tx) => {
    setEditingTx(tx);
    setFormData({
      entity_id: tx.entity_id || '', type: tx.type || 'income', category: tx.category || '',
      description: tx.description || '', amount: Math.abs(tx.amount) || '',
      date: tx.date || '', payment_method: tx.payment_method || 'bank_transfer'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingTx(null);
    setFormData({ entity_id: '', type: 'income', category: 'rent', description: '', amount: '', date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer' });
  };

  const exportTransactions = async () => {
    try {
      // Fetch all transactions for export
      const params = new URLSearchParams();
      params.append('page', 1);
      params.append('limit', 10000);
      if (filterType) params.append('transaction_type', filterType);
      if (filterProperty) params.append('entity_id', filterProperty);
      
      const res = await axios.get(`${API}/transactions?${params}`);
      const allTx = res.data.data || res.data;
      
      let csv = 'Date,Type,Description,Property,Category,Method,Amount\n';
      allTx.forEach(tx => {
        csv += `${tx.date},${tx.type},"${tx.description}","${tx.entity_name || ''}",${tx.category || ''},${tx.payment_method || ''},${tx.amount}\n`;
      });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleFilterChange = (type, value) => {
    if (type === 'type') setFilterType(value);
    if (type === 'property') setFilterProperty(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.pages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount || 0);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
          <p className="text-slate-500">Track income and expenses • {pagination.total.toLocaleString()} total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportTransactions} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg" data-testid="add-transaction-btn">
            <Plus className="w-4 h-4" /> Add Transaction
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-100 p-2 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Income (Property)' : 'Page Income'}</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(stats.income)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <div className="bg-rose-100 p-2 rounded-lg"><TrendingDown className="w-5 h-5 text-rose-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Expenses (Property)' : 'Page Expenses'}</p>
              <p className="text-xl font-bold text-rose-600">{formatCurrency(stats.expenses)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-100 p-2 rounded-lg"><DollarSign className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Net Income (Property)' : 'Page Net'}</p>
              <p className={`text-xl font-bold ${stats.income - stats.expenses >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(stats.income - stats.expenses)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <select value={filterType} onChange={(e) => handleFilterChange('type', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Types</option>
          <option value="income">Income</option>
          <option value="expense">Expenses</option>
        </select>
        <select value={filterProperty} onChange={(e) => handleFilterChange('property', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Edit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-600">{tx.date}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${tx.type === 'income' ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                              {tx.type === 'income' ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-rose-600" />}
                            </div>
                            <span className="text-sm font-medium text-slate-900">{tx.description}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{tx.entity_name}</td>
                        <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{tx.category}</span></td>
                        <td className="px-4 py-3 text-sm text-slate-600">{tx.payment_method}</td>
                        <td className="px-4 py-3 text-right"><span className={`font-semibold ${tx.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{tx.type === 'income' ? '+' : ''}{formatCurrency(tx.amount)}</span></td>
                        <td className="px-4 py-3 text-center"><button onClick={() => openEditModal(tx)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"><Edit className="w-4 h-4" /></button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <div className="text-sm text-slate-600">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()} transactions
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 border border-slate-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm ${
                            pagination.page === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'border border-slate-300 hover:bg-white'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => goToPage(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 border border-slate-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{editingTx ? 'Edit Transaction' : 'Add Transaction'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label><select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="income">Income</option><option value="expense">Expense</option></select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Property</label><select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="">Select property</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><input type="text" required value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Transaction description" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label><select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="rent">Rent</option><option value="maintenance_fee">Maintenance Fee</option><option value="parking">Parking</option><option value="repairs">Repairs</option><option value="utilities">Utilities</option><option value="insurance">Insurance</option><option value="management">Management</option><option value="other">Other</option></select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount</label><input type="number" required step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Date</label><input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label><select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="credit_card">Credit Card</option><option value="eft">EFT</option></select></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingTx ? 'Save Changes' : 'Add Transaction'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
